import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface MultilingualName {
  en: string;
  ar: string;
  _id: string;
}

export interface User {
  id: string;
  full_name: string;
  email: string | null;
  country_code: string | null;
  phone_number: string;
  profile_image: string | null;
  dob: string;
  gender: string;
  address: string;
  device_type: string | null;
  device_token: string | null;
  total_available_games: number;
  IQ: number | null;
  status: boolean;
  created_at: string;
  updated_at: string;
  is_free_game_played: boolean;
  social_type: string | null;
  register_from: string | null;
  is_email_verified?: boolean;
  display_achievement_name: MultilingualName | null;
  display_category_name: MultilingualName | null;
  new_achievement_earned?: boolean;
}

// API response user type (handles both _id and id from different endpoints)
export interface ApiUser {
  id?: string;
  _id?: string;
  full_name: string;
  dob: string;
  gender: string;
  address: string;
  email: string | null;
  country_code: string | null;
  phone_number: string;
  profile_image?: string | null;
  IQ?: number | null;
  device_type?: string | null;
  device_token?: string | null;
  total_available_games?: number;
  status: boolean;
  is_free_game_played: boolean;
  created_at: string;
  updated_at: string;
  social_type: string | null;
  register_from: string | null;
  is_email_verified?: boolean;
  display_achievement_name?: MultilingualName | null;
  display_category_name?: MultilingualName | null;
  new_achievement_earned?: boolean;
}

interface UserState {
  user: User | null;
  isAuthenticated: boolean;
}

const initialState: UserState = {
  user: null,
  isAuthenticated: false,
};

// Helper to normalize API user to our User type
const normalizeUser = (apiUser: ApiUser): User => ({
  id: apiUser.id || apiUser._id || "",
  full_name: apiUser.full_name,
  dob: apiUser.dob,
  gender: apiUser.gender,
  address: apiUser.address,
  email: apiUser.email,
  country_code: apiUser.country_code || null,
  phone_number: apiUser.phone_number,
  profile_image: apiUser.profile_image as string | null,
  IQ: apiUser.IQ ?? null,
  device_type: apiUser.device_type || null,
  device_token: apiUser.device_token || null,
  total_available_games: apiUser.total_available_games || 0,
  status: apiUser.status,
  created_at: apiUser.created_at,
  updated_at: apiUser.updated_at,
  is_free_game_played: apiUser.is_free_game_played || false,
  social_type: apiUser.social_type || null,
  register_from: apiUser.register_from || null,
  is_email_verified: apiUser.is_email_verified !== undefined ? apiUser.is_email_verified : true,
  display_achievement_name: apiUser.display_achievement_name || null,
  display_category_name: apiUser.display_category_name || null,
  new_achievement_earned: Boolean(apiUser.new_achievement_earned),
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<ApiUser>) => {
      state.user = normalizeUser(action.payload);
      state.isAuthenticated = true;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem("token");
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

export const { setUser, clearUser, updateUser } = userSlice.actions;
export default userSlice.reducer;
